import { useEffect, useRef, useState } from "react";
import { CanvasModel } from "../../core/canvasModel";
import type { KarelModelShape } from "../../core/karelModel";
import { KarelView } from "../../runtime/karelView";
import { loadWorldModel } from "../../util/worldModel";
import { useCanvasRepaint } from "../../ide/useCanvasRepaint";
import { useKarelImagesReady } from "./useKarelImagesReady";
import styles from "./WorldThumbnail.module.css";

const THUMBNAIL_SIZE = 240;

interface WorldThumbnailProps {
  world: string;
  label?: string;
}

type Status = "loading" | "ready" | "error";

interface ParsedWorld {
  model: KarelModelShape;
  canvasModel: CanvasModel;
}

/**
 * Parsed worlds are cached per name so toggling the goal overlay back on does
 * not re-parse the same world. Keyed by world name; safe because thumbnails are
 * read-only and always render at `THUMBNAIL_SIZE`.
 */
const parsedCache = new Map<string, ParsedWorld>();

function parseWorld(world: string): ParsedWorld {
  const canvasModel = CanvasModel(THUMBNAIL_SIZE, THUMBNAIL_SIZE);
  const model = loadWorldModel(world, canvasModel);
  return { model, canvasModel };
}

/**
 * Renders a single Karel world to a small canvas, reusing the same model and
 * `KarelView` renderer as the live IDE so previews can never drift from the
 * world files. Redraws when the active theme changes.
 */
export function WorldThumbnail({ world, label }: WorldThumbnailProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const modelRef = useRef<KarelModelShape | null>(null);
  const canvasModelRef = useRef<CanvasModel | null>(null);
  const imagesReady = useKarelImagesReady();
  const [status, setStatus] = useState<Status>("loading");

  useEffect(() => {
    if (!imagesReady) return;
    setStatus("loading");
    modelRef.current = null;
    canvasModelRef.current = null;

    try {
      const parsed = parsedCache.get(world) ?? parseWorld(world);
      parsedCache.set(world, parsed);
      modelRef.current = parsed.model;
      canvasModelRef.current = parsed.canvasModel;
      draw();
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  }, [world, imagesReady]);

  useCanvasRepaint(draw);

  function draw(): void {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    const model = modelRef.current;
    const canvasModel = canvasModelRef.current;
    if (!canvas || !context || !model || !canvasModel) return;
    context.clearRect(0, 0, canvas.width, canvas.height);
    KarelView.draw(canvasModel, model, context);
  }

  if (status === "error") {
    return (
      <span className={styles.root}>
        <span className={styles.error}>
          Preview unavailable. Please refresh the page.
        </span>
      </span>
    );
  }

  return (
    <span className={styles.root}>
      {status === "loading" && (
        <span className={styles.skeleton} aria-hidden="true" />
      )}
      <canvas
        ref={canvasRef}
        className={styles.canvas}
        width={THUMBNAIL_SIZE}
        height={THUMBNAIL_SIZE}
        role="img"
        aria-label={label ?? `Preview of world ${world}`}
      />
    </span>
  );
}
